import React from 'react';

import { Box, Button, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';

import type { PartnerSubmittedRoomAssetMatch } from '../../utils';

function formatMax(value: number | string | null | undefined) {
  return value === null || value === undefined || value === '' ? '—' : value;
}

interface Props {
  roomAsset?: PartnerSubmittedRoomAssetMatch;
  offerId?: string;
}

export default function MaximumCapacitiesSummary({ roomAsset: match, offerId }: Props) {
  const roomAsset = match?.roomAsset;
  const maxCapacity = formatMax(roomAsset?.maximum_occupancy_number__c);
  const maxAdults = formatMax(roomAsset?.max_adults__c);
  const maxChildren = formatMax(roomAsset?.max_children__c);
  const maxTeenagers = formatMax(roomAsset?.max_teenagers__c);
  const maxInfants = formatMax(roomAsset?.max_infants__c);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Maximum capacities</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {match && !match.isExactMatch && (
            <Tooltip title="No room asset with an exact matching name was found — this is the closest name match, and may be for a different room.">
              <Chip label="Best guess match" size="small" color="warning" variant="outlined" />
            </Tooltip>
          )}
          <Chip label="Read only" size="small" variant="outlined" />
          <Button
            href={
              roomAsset && offerId
                ? `/offers/${offerId}/offer-builder/assets${roomAsset.sfid ? `?sfid=${roomAsset.sfid}` : ''}`
                : undefined
            }
            size="small"
            variant="text"
            disabled={!roomAsset || !offerId}
          >
            Room asset
          </Button>
        </Stack>
      </Stack>

      <Box display="grid" gap={3} gridTemplateColumns="repeat(5, 1fr)">
        <Box>
          <Typography variant="caption" color="text.secondary" textTransform="uppercase">
            Max capacity
          </Typography>
          <Typography variant="h6">{maxCapacity}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" textTransform="uppercase">
            Max adults
          </Typography>
          <Typography variant="h6">{maxAdults}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" textTransform="uppercase">
            Max children
          </Typography>
          <Typography variant="h6">{maxChildren}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" textTransform="uppercase">
            Max teenagers
          </Typography>
          <Typography variant="h6">{maxTeenagers}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" textTransform="uppercase">
            Max infants
          </Typography>
          <Typography variant="h6">{maxInfants}</Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        From the partner-submitted room form. Per-type maxima apply within the overall capacity, so they do not add up
        to it.
      </Typography>
    </Paper>
  );
}
